import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy
} from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL } from '../../firebase/config';
import {
  FaArrowLeft,
  FaSpinner,
  FaEnvelope,
  FaCheck,
  FaTrash,
  FaEye,
  FaTimes,
  FaInbox,
  FaCheckCircle,
  FaClock,
  FaUser,
  FaBuilding,
  FaCalendarAlt,
  FaReply
} from 'react-icons/fa';
import './AdminEnquiries.css';

const AdminEnquiries = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, done
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        setUser(currentUser);
        fetchEnquiries();
      } else {
        navigate('/admin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchEnquiries = async () => {
    try {
      const q = query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setEnquiries(items);
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      setError('Failed to load enquiries');
    }
  };

  const handleMarkAsDone = async (id) => {
    try {
      await updateDoc(doc(db, 'contactMessages', id), {
        status: 'done',
        read: true,
        completedAt: new Date().toISOString()
      });
      setSuccess('Enquiry marked as done!');
      await fetchEnquiries();
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry(prev => ({ ...prev, status: 'done', read: true }));
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating enquiry:', err);
      setError('Failed to update enquiry');
    }
  };

  const handleMarkAsNew = async (id) => {
    try {
      await updateDoc(doc(db, 'contactMessages', id), {
        status: 'new',
        read: false,
        completedAt: null
      });
      setSuccess('Enquiry marked as new!');
      await fetchEnquiries();
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry(prev => ({ ...prev, status: 'new', read: false }));
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating enquiry:', err);
      setError('Failed to update enquiry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'contactMessages', id));
      setSuccess('Enquiry deleted!');
      await fetchEnquiries();
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry(null);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      setError('Failed to delete enquiry');
    }
  };

  const handleViewEnquiry = async (enquiry) => {
    setSelectedEnquiry(enquiry);

    // Mark as read if it's new
    if (!enquiry.read) {
      try {
        await updateDoc(doc(db, 'contactMessages', enquiry.id), {
          read: true
        });
        await fetchEnquiries();
      } catch (err) {
        console.error('Error marking as read:', err);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    if (filter === 'all') return true;
    if (filter === 'new') return enquiry.status === 'new' || !enquiry.status;
    if (filter === 'done') return enquiry.status === 'done';
    return true;
  });

  const newCount = enquiries.filter(e => e.status === 'new' || !e.status).length;
  const doneCount = enquiries.filter(e => e.status === 'done').length;

  if (loading) {
    return (
      <div className="admin-loading">
        <FaSpinner className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="admin-enquiries">
      <header className="admin-enquiries-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <FaArrowLeft /> Back to Dashboard
        </button>
        <h1><FaEnvelope /> Contact Enquiries</h1>
      </header>

      <main className="admin-enquiries-content">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="enquiries-stats">
          <div className="stat-card total">
            <FaInbox />
            <div className="stat-info">
              <span className="stat-number">{enquiries.length}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
          <div className="stat-card new">
            <FaClock />
            <div className="stat-info">
              <span className="stat-number">{newCount}</span>
              <span className="stat-label">New</span>
            </div>
          </div>
          <div className="stat-card done">
            <FaCheckCircle />
            <div className="stat-info">
              <span className="stat-number">{doneCount}</span>
              <span className="stat-label">Done</span>
            </div>
          </div>
        </div>

        <div className="enquiries-filter">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({enquiries.length})
          </button>
          <button
            className={`filter-btn ${filter === 'new' ? 'active' : ''}`}
            onClick={() => setFilter('new')}
          >
            New ({newCount})
          </button>
          <button
            className={`filter-btn ${filter === 'done' ? 'active' : ''}`}
            onClick={() => setFilter('done')}
          >
            Done ({doneCount})
          </button>
        </div>

        <div className="enquiries-layout">
          <div className="enquiries-list">
            {filteredEnquiries.length === 0 ? (
              <div className="empty-state">
                <FaInbox />
                <h3>No enquiries found</h3>
                <p>{filter === 'all' ? 'No contact enquiries yet.' : `No ${filter} enquiries.`}</p>
              </div>
            ) : (
              filteredEnquiries.map((enquiry) => (
                <div
                  key={enquiry.id}
                  className={`enquiry-item ${!enquiry.read ? 'unread' : ''} ${selectedEnquiry?.id === enquiry.id ? 'selected' : ''} ${enquiry.status === 'done' ? 'done' : ''}`}
                  onClick={() => handleViewEnquiry(enquiry)}
                >
                  <div className="enquiry-item-status">
                    {enquiry.status === 'done' ? (
                      <FaCheckCircle className="status-icon done" />
                    ) : (
                      <FaClock className="status-icon new" />
                    )}
                  </div>
                  <div className="enquiry-item-content">
                    <div className="enquiry-item-header">
                      <h4 className="enquiry-name">{enquiry.name}</h4>
                      <span className="enquiry-date">{formatDate(enquiry.createdAt)}</span>
                    </div>
                    <p className="enquiry-email">{enquiry.email}</p>
                    <p className="enquiry-preview">{enquiry.message.substring(0, 80)}...</p>
                  </div>
                  <div className="enquiry-item-actions">
                    {enquiry.status !== 'done' ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkAsDone(enquiry.id); }}
                        className="action-btn done-btn"
                        title="Mark as done"
                      >
                        <FaCheck />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkAsNew(enquiry.id); }}
                        className="action-btn reopen-btn"
                        title="Mark as new"
                      >
                        <FaClock />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(enquiry.id); }}
                      className="action-btn delete-btn"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="enquiry-detail">
            {selectedEnquiry ? (
              <>
                <div className="enquiry-detail-header">
                  <h2>Enquiry Details</h2>
                  <button onClick={() => setSelectedEnquiry(null)} className="close-btn">
                    <FaTimes />
                  </button>
                </div>

                <div className="enquiry-detail-content">
                  <div className="detail-status">
                    <span className={`status-badge ${selectedEnquiry.status === 'done' ? 'done' : 'new'}`}>
                      {selectedEnquiry.status === 'done' ? (
                        <><FaCheckCircle /> Done</>
                      ) : (
                        <><FaClock /> New</>
                      )}
                    </span>
                  </div>

                  <div className="detail-row">
                    <div className="detail-icon"><FaUser /></div>
                    <div className="detail-info">
                      <label>Name</label>
                      <p>{selectedEnquiry.name}</p>
                    </div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-icon"><FaEnvelope /></div>
                    <div className="detail-info">
                      <label>Email</label>
                      <p>
                        <a href={`mailto:${selectedEnquiry.email}`}>{selectedEnquiry.email}</a>
                      </p>
                    </div>
                  </div>

                  {selectedEnquiry.company && (
                    <div className="detail-row">
                      <div className="detail-icon"><FaBuilding /></div>
                      <div className="detail-info">
                        <label>Company</label>
                        <p>{selectedEnquiry.company}</p>
                      </div>
                    </div>
                  )}

                  <div className="detail-row">
                    <div className="detail-icon"><FaCalendarAlt /></div>
                    <div className="detail-info">
                      <label>Received</label>
                      <p>{formatDate(selectedEnquiry.createdAt)}</p>
                    </div>
                  </div>

                  <div className="detail-message">
                    <label>Message</label>
                    <div className="message-content">
                      {selectedEnquiry.message}
                    </div>
                  </div>

                  <div className="detail-actions">
                    <a
                      href={`mailto:${selectedEnquiry.email}?subject=Re: Your enquiry to OPTEAM Services&body=Dear ${selectedEnquiry.name},%0D%0A%0D%0AThank you for contacting us.%0D%0A%0D%0A`}
                      className="reply-btn"
                    >
                      <FaReply /> Reply via Email
                    </a>
                    {selectedEnquiry.status !== 'done' ? (
                      <button
                        onClick={() => handleMarkAsDone(selectedEnquiry.id)}
                        className="mark-done-btn"
                      >
                        <FaCheck /> Mark as Done
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMarkAsNew(selectedEnquiry.id)}
                        className="mark-new-btn"
                      >
                        <FaClock /> Mark as New
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <FaEye />
                <h3>Select an enquiry</h3>
                <p>Click on an enquiry from the list to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEnquiries;
